import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dentroDaRaiz, dentroDeAlgumaRaiz, ROTAS, type RaizPermitida } from '@remoto/shared';
import { chamar } from '../api';
import { CampoDeEscolha } from '../componentes/campos';
import {
  IconeArquivo,
  IconeCirculo,
  IconePasta,
  IconePonto,
  IconePreso,
} from '../componentes/icones';
import { TIPO_ARRASTO } from './estado';
import { mensagemDoErro } from '../erros';

export interface ItemDaArvore {
  nome: string;
  caminho: string;
  tipo: 'pasta' | 'arquivo';
  tamanho: number | null;
  negado: boolean;
}

export interface MaquinaComRaizes {
  id: string;
  nome: string;
  estado: string;
  raizesPermitidas: RaizPermitida[];
}

/** A pasta acima, desde que ainda esteja dentro de alguma raiz liberada. */
export function pastaAcima(pasta: string, raizes: RaizPermitida[]): string | null {
  if (!pasta) return null;
  const partes = pasta.split(/[\\/]/);
  partes.pop();
  const acima = partes.join(pasta.includes('\\') ? '\\' : '/');
  return dentroDeAlgumaRaiz(acima, raizes) ? acima : null;
}

/**
 * Onde o explorador deve abrir.
 *
 * A pasta da sessão vence, porque é onde a pessoa estava trabalhando — mas só
 * se o administrador liberou aquele caminho nesta máquina. Fora disso, a
 * primeira raiz, que é o comportamento antigo.
 */
export function pastaInicial(pastaDaSessao: string, raizes: RaizPermitida[]): string {
  if (dentroDeAlgumaRaiz(pastaDaSessao, raizes)) return pastaDaSessao;
  return raizes[0]?.caminho ?? '';
}

/**
 * Árvore de pastas de uma máquina.
 *
 * Uma pasta por vez, como na tela antiga: a árvore inteira de um projeto grande
 * não cabe, e o daemon lista sob demanda. O que muda aqui é o lugar — ela fica
 * ao lado do editor e da conversa em vez de numa página só dela.
 */
export function Explorador({
  maquinaId,
  pasta,
  arquivoAtivo,
  pastaDaSessao,
  tocados,
  aoTrocarMaquina,
  aoAbrirPasta,
  aoAbrirArquivo,
}: {
  maquinaId: string;
  pasta: string;
  arquivoAtivo: string;
  /** O `cwd` da sessão escolhida: é onde o explorador começa. */
  pastaDaSessao: string;
  /** Arquivos que a sessão editou nesta visita: ganham marca na árvore. */
  tocados: Set<string>;
  aoTrocarMaquina: (id: string) => void;
  aoAbrirPasta: (caminho: string) => void;
  aoAbrirArquivo: (caminho: string) => void;
}): React.JSX.Element {
  /**
   * Duas visões: a da sessão e a da máquina.
   *
   * "Sessão" é o padrão porque é o que se quer em 90% das vezes, e porque a
   * primeira raiz de uma máquina de trabalho pode ser uma pasta de clientes
   * com mil itens — abrir nela é abrir no lugar errado, com barulho.
   */
  const [subaba, setSubaba] = useState<'sessao' | 'sistema'>('sessao');
  const { data: maquinas = [] } = useQuery({
    queryKey: ['maquinas'],
    queryFn: () => chamar<MaquinaComRaizes[]>(ROTAS.maquinas),
  });

  const maquina = maquinas.find((m) => m.id === maquinaId) ?? maquinas[0];

  // Primeira visita: escolhe a máquina e a pasta sozinho, para a lateral não
  // abrir vazia esperando dois cliques.
  useEffect(() => {
    if (!maquina) return;
    if (maquina.id !== maquinaId) aoTrocarMaquina(maquina.id);
    if (!pasta) {
      const inicial = pastaInicial(pastaDaSessao, maquina.raizesPermitidas);
      if (inicial) aoAbrirPasta(inicial);
    }
  }, [maquina, maquinaId, pasta, pastaDaSessao, aoTrocarMaquina, aoAbrirPasta]);

  const {
    data: arvore,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['arvore', maquina?.id, pasta],
    queryFn: () =>
      chamar<{ caminho: string; itens: ItemDaArvore[] }>(
        `${ROTAS.maquinas}/${maquina?.id}/arquivos?caminho=${encodeURIComponent(pasta)}`,
      ),
    enabled: Boolean(maquina?.id) && pasta.length > 0,
    retry: false,
  });

  const naSessao = subaba === 'sessao' && Boolean(pastaDaSessao);
  const acima = maquina ? pastaAcima(pasta, maquina.raizesPermitidas) : null;
  // Na visão da sessão o `..` para na pasta de trabalho dela: subir para fora
  // é justamente o que a outra aba serve para fazer, e de propósito.
  const podeSubir = acima !== null && (!naSessao || dentroDaRaiz(acima, pastaDaSessao));
  const recado =
    error ? mensagemDoErro(error, 'Não consegui listar esta pasta.') : '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      {pastaDaSessao ? (
        <div className="flex shrink-0 gap-1 px-2 pt-2 text-[11px]">
          {(
            [
              { id: 'sessao', rotulo: 'Sessão' },
              { id: 'sistema', rotulo: 'Raízes' },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setSubaba(item.id);
                if (item.id === 'sessao' && !dentroDaRaiz(pasta, pastaDaSessao)) {
                  aoAbrirPasta(pastaDaSessao);
                }
              }}
              aria-current={subaba === item.id}
              className={
                'rounded px-2 py-0.5 transition ' +
                (subaba === item.id
                  ? 'bg-zinc-800 text-zinc-200'
                  : 'text-zinc-500 hover:text-zinc-300')
              }
            >
              {item.rotulo}
            </button>
          ))}
        </div>
      ) : null}

      <div className={'shrink-0 px-2 py-2 ' + (naSessao ? '' : 'border-b border-zinc-800')}>
        {naSessao ? null : (
          <CampoDeEscolha
            // A barra é estreita demais para um rótulo escrito, mas quem lê por
            // leitor de tela continua precisando saber para que serve o campo.
            rotulo="Máquina"
            rotuloOculto
            valor={maquina?.id ?? null}
            vazio="nenhuma máquina"
            opcoes={maquinas.map((m) => ({
              id: m.id,
              rotulo: m.nome,
              /**
               * O estado vira ícone e cor. Como `●`/`○` no fim do rótulo ele
               * grudava no nome da máquina e era lido em voz alta como
               * "desktop-birunce círculo preto".
               */
              icone:
                m.estado === 'ONLINE' ? (
                  <IconePonto tamanho={12} className="text-emerald-400" />
                ) : (
                  <IconeCirculo tamanho={12} className="text-zinc-600" />
                ),
              descricao: m.estado === 'ONLINE' ? 'no ar' : 'fora do ar',
            }))}
            aoEscolher={aoTrocarMaquina}
          />
        )}
        <div className={'flex flex-wrap gap-1 ' + (naSessao ? '' : 'mt-1.5')}>
          {/* Na visão da sessão o único atalho é a pasta dela. */}
          {(naSessao
            ? [{ caminho: pastaDaSessao, modo: 'rw' as const }]
            : (maquina?.raizesPermitidas ?? [])
          ).map((raiz) => (
            <button
              key={raiz.caminho}
              onClick={() => aoAbrirPasta(raiz.caminho)}
              title={`${raiz.caminho} (${raiz.modo})`}
              className={
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition ' +
                (dentroDaRaiz(pasta, raiz.caminho)
                  ? 'bg-zinc-800 text-zinc-200'
                  : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300')
              }
            >
              {raiz.caminho.split(/[\\/]/).filter(Boolean).at(-1) ?? raiz.caminho}
              {raiz.modo === 'ro' ? <IconePreso tamanho={12} /> : null}
            </button>
          ))}
        </div>
      </div>

      <p className="shrink-0 truncate px-3 py-1 font-mono text-[11px] text-zinc-600" title={pasta}>
        {pasta || '—'}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
        {recado ? <p className="px-2 py-1 text-xs text-amber-300">{recado}</p> : null}
        {isLoading ? <p className="px-2 py-1 text-xs text-zinc-500">carregando…</p> : null}

        <ul className="space-y-px text-sm">
          {podeSubir && acima ? (
            <li>
              <button
                onClick={() => aoAbrirPasta(acima)}
                className="w-full rounded px-2 py-0.5 text-left text-zinc-500 hover:bg-zinc-800"
              >
                ..
              </button>
            </li>
          ) : null}
          {(arvore?.itens ?? []).map((item) => (
            <li key={item.caminho}>
              <button
                onClick={() =>
                  item.tipo === 'pasta' ? aoAbrirPasta(item.caminho) : aoAbrirArquivo(item.caminho)
                }
                // Arrastar um arquivo daqui para a direita do editor divide a
                // área — é o mesmo gesto de arrastar uma aba.
                draggable={item.tipo === 'arquivo' && !item.negado}
                onDragStart={(e) => {
                  e.dataTransfer.setData(TIPO_ARRASTO, item.caminho);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                disabled={item.negado}
                title={item.negado ? 'guarda credencial: não sai da máquina' : item.caminho}
                className={
                  'flex w-full items-center gap-1.5 rounded px-2 py-0.5 text-left transition ' +
                  (item.negado
                    ? 'cursor-not-allowed text-zinc-600'
                    : item.caminho === arquivoAtivo
                      ? 'bg-zinc-800 text-zinc-100'
                      : item.tipo === 'pasta'
                        ? 'text-zinc-200 hover:bg-zinc-800'
                        : 'text-zinc-400 hover:bg-zinc-800')
                }
              >
                <span className="flex shrink-0 text-zinc-600">
                  {item.negado ? (
                    <IconePreso tamanho={14} />
                  ) : item.tipo === 'pasta' ? (
                    <IconePasta tamanho={14} />
                  ) : (
                    <IconeArquivo tamanho={14} />
                  )}
                </span>
                <span className="truncate">{item.nome}</span>
                {tocados.has(item.caminho) ? (
                  <span
                    title="a sessão editou este arquivo"
                    className="ml-auto flex shrink-0 text-emerald-400"
                  >
                    <IconePonto tamanho={12} />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
